import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { handleGoogleSignOut, signInWithGoogle } from "../utils/firebase.utils";
import { useAuth } from "../contexts/AuthContext";
import { useRequireAuth } from "../hooks/useRequireAuth";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";
import { LogOut, Settings, Building2, Droplets } from "lucide-react";

/**
 * `variant="minimal"` is the redesigned header's treatment: a mono "Sign in"
 * text button instead of the blue pill, and a neutral ring on the avatar. The
 * auth behaviour is identical — only the chrome changes.
 *
 * Signed-in state comes from `useAuth()`, not a `token` prop: the prop was
 * `localStorage.accessToken`, written once at sign-in and never refreshed.
 *
 * Signing in here leaves the user where they are, so an address in the URL
 * survives it. Only the marketing homepage moves on to `/app` afterwards —
 * client-side, the destination it has always had.
 */
const SignIn = (props) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { status, user } = useAuth();
  const minimal = props.variant === "minimal";
  // The menu's two destinations are gated pages. The menu only shows signed
  // in, but a session can lapse under an open menu, so they still go through
  // the gate rather than assuming.
  const requireSettings = useRequireAuth("account-settings");
  const requireProperties = useRequireAuth("my-properties");

  const login = () =>
    signInWithGoogle()
      .then(() => {
        if (pathname === "/") navigate("/app");
      })
      // The toast is the whole of the feedback for a header sign-in; the
      // modal's wording is for gated actions.
      .catch(() => {});

  const logout = async () => {
    await handleGoogleSignOut();
  };

  // Nothing until Firebase has restored the session: a "Sign in" button that
  // turns into an avatar a beat later reads as having been signed out.
  if (status === "loading") return null;

  const photo = user?.photoURL || localStorage.getItem("photoURL");
  const name = user?.displayName || localStorage.getItem("displayName");

  return (
    <>
      {status === "signed-in" ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className={`w-10 h-10 gap-0 p-0 bg-transparent rounded-full ${
                minimal ? "border border-zp-ink/20" : "border-2 border-blue-600"
              }`}
            >
              <Avatar className="h-8 w-8">
                <AvatarImage src={photo} alt={name} />
                <AvatarFallback className="bg-muted text-muted-foreground text-xs">
                  {name?.charAt(0) || "U"}
                </AvatarFallback>
              </Avatar>
              {/* <span className="max-w-[100px] truncate">
                {localStorage.getItem("displayName")?.split(" ")[0] ||
                  "Profile"}
              </span> */}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem
              onClick={() => requireSettings(() => navigate("/app/settings"))}
              className="text-sm"
            >
              <Settings className="mr-2 h-4 w-4" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => requireProperties(() => navigate("/app/my-properties"))}
              className="text-sm"
            >
              <Building2 className="mr-2 h-4 w-4" />
              My Properties
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="text-sm">
              <LogOut className="mr-2 h-4 w-4" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : minimal ? (
        <button
          type="button"
          onClick={login}
          className="whitespace-nowrap font-mono text-[10.5px] uppercase tracking-[0.133em] text-zp-slate transition-colors hover:text-zp-ink"
        >
          Sign in
        </button>
      ) : (
        <Button
          variant="outline"
          className="rounded-full text-blue-600 border-blue-600 hover:bg-blue-600 hover:text-white transition-colors gap-2"
          onClick={login}
        >
          <Droplets className="h-4 w-4" />
          Free Access
        </Button>
      )}
    </>
  );
};

export default SignIn;
